import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useJsApiLoader,
} from "@react-google-maps/api";

import {
    Row,
    Col,
    Input,
    Button,
    Card,
    CardBody,
    Spinner,
    Badge,
} from "reactstrap";

import axios from "../../../../../utils/api";

import {
    FaArrowLeft,
    FaMapMarkerAlt,
    FaUsers,
    FaCalendarAlt,
    FaClock,
    FaSuitcase,
    FaCar,
    FaTag,
    FaSearch,
    FaRoute,
} from "react-icons/fa";


/* =====================================
   GOOGLE MAPS LIBRARIES
===================================== */

const libraries = [
    "places",
];


const CabBookingSearchPage = ({
    tenantId,
    property,
    onBack,
}) => {


    /* =====================================
       GOOGLE MAPS LOADER
    ===================================== */

    const {
        isLoaded,
        loadError,
    } = useJsApiLoader({

        id:
            "google-maps-script",

        googleMapsApiKey:
            process.env
                .REACT_APP_GOOGLE_MAPS_API_KEY,

        libraries,

    });


    /* =====================================
       GOOGLE AUTOCOMPLETE REFERENCES
    ===================================== */

    const pickupInputRef =
        useRef(null);

    const dropInputRef =
        useRef(null);

    const pickupAutocompleteRef =
        useRef(null);

    const dropAutocompleteRef =
        useRef(null);

    const pickupListenerRef =
        useRef(null);

    const dropListenerRef =
        useRef(null);


    /* =====================================
       SEARCH DATA
    ===================================== */

    const [
        searchData,
        setSearchData,
    ] = useState({

        pickupLocation: "",

        dropLocation: "",

        pickupLat: null,

        pickupLng: null,

        dropLat: null,

        dropLng: null,

        numberOfPax: 1,

        journeyDate: "",

        journeyTime: "",

        distance: 0,

        estimatedDuration: "",

    });


    /* =====================================
       SEARCH RESULTS
    ===================================== */

    const [
        cabs,
        setCabs,
    ] = useState([]);


    const [
        offers,
        setOffers,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        calculatingRoute,
        setCalculatingRoute,
    ] = useState(false);


    const [
        searched,
        setSearched,
    ] = useState(false);


    const [
        error,
        setError,
    ] = useState("");


    /* =====================================
       HANDLE NORMAL INPUT
    ===================================== */

    const handleChange =
        (event) => {

            const {
                name,
                value,
            } = event.target;


            setSearchData(
                (previous) => ({

                    ...previous,

                    [name]:
                        value,

                })
            );

        };


    /* =====================================
       INITIALIZE GOOGLE AUTOCOMPLETE
    ===================================== */

    useEffect(
        () => {

            if (
                !isLoaded ||
                !window.google ||
                !window.google.maps ||
                !window.google.maps.places
            ) {

                return;

            }


            /*
               PICKUP AUTOCOMPLETE
            */

            if (
                pickupInputRef.current &&
                !pickupAutocompleteRef.current
            ) {

                const pickupAutocomplete =
                    new window.google.maps
                        .places
                        .Autocomplete(

                            pickupInputRef.current,

                            {

                                fields: [

                                    "geometry",

                                    "formatted_address",

                                    "name",

                                ],

                            }

                        );


                pickupAutocompleteRef.current =
                    pickupAutocomplete;


                pickupListenerRef.current =
                    pickupAutocomplete
                        .addListener(

                            "place_changed",

                            () => {

                                const place =
                                    pickupAutocomplete
                                        .getPlace();


                                console.log(
                                    "📍 PICKUP PLACE:",
                                    place
                                );


                                if (
                                    !place ||
                                    !place.geometry ||
                                    !place.geometry.location
                                ) {

                                    return;

                                }


                                const pickupLat =
                                    place
                                        .geometry
                                        .location
                                        .lat();


                                const pickupLng =
                                    place
                                        .geometry
                                        .location
                                        .lng();


                                const pickupLocation =
                                    place
                                        .formatted_address ||
                                    place
                                        .name ||
                                    "";


                                setSearchData(
                                    (
                                        previous
                                    ) => ({

                                        ...previous,

                                        pickupLocation,

                                        pickupLat,

                                        pickupLng,

                                        distance:
                                            0,

                                        estimatedDuration:
                                            "",

                                    })
                                );


                                setError("");

                            }

                        );

            }


            /*
               DROP AUTOCOMPLETE
            */

            if (
                dropInputRef.current &&
                !dropAutocompleteRef.current
            ) {

                const dropAutocomplete =
                    new window.google.maps
                        .places
                        .Autocomplete(

                            dropInputRef.current,

                            {

                                fields: [

                                    "geometry",

                                    "formatted_address",

                                    "name",

                                ],

                            }

                        );


                dropAutocompleteRef.current =
                    dropAutocomplete;


                dropListenerRef.current =
                    dropAutocomplete
                        .addListener(

                            "place_changed",

                            () => {

                                const place =
                                    dropAutocomplete
                                        .getPlace();


                                console.log(
                                    "📍 DROP PLACE:",
                                    place
                                );


                                if (
                                    !place ||
                                    !place.geometry ||
                                    !place.geometry.location
                                ) {

                                    return;

                                }


                                const dropLat =
                                    place
                                        .geometry
                                        .location
                                        .lat();


                                const dropLng =
                                    place
                                        .geometry
                                        .location
                                        .lng();


                                const dropLocation =
                                    place
                                        .formatted_address ||
                                    place
                                        .name ||
                                    "";


                                setSearchData(
                                    (
                                        previous
                                    ) => ({

                                        ...previous,

                                        dropLocation,

                                        dropLat,

                                        dropLng,

                                        distance:
                                            0,

                                        estimatedDuration:
                                            "",

                                    })
                                );


                                setError("");

                            }

                        );

            }


            /*
               CLEANUP
            */

            return () => {

                if (
                    pickupListenerRef.current
                ) {

                    pickupListenerRef
                        .current
                        .remove();

                }


                if (
                    dropListenerRef.current
                ) {

                    dropListenerRef
                        .current
                        .remove();

                }

            };

        },

        [
            isLoaded,
        ]

    );


    /* =====================================
       HANDLE PICKUP TYPING
    ===================================== */

    const handlePickupChange =
        (event) => {

            const value =
                event.target.value;


            setSearchData(
                (previous) => ({

                    ...previous,

                    pickupLocation:
                        value,

                    pickupLat:
                        null,

                    pickupLng:
                        null,

                    distance:
                        0,

                    estimatedDuration:
                        "",

                })
            );

        };


    /* =====================================
       HANDLE DROP TYPING
    ===================================== */

    const handleDropChange =
        (event) => {

            const value =
                event.target.value;


            setSearchData(
                (previous) => ({

                    ...previous,

                    dropLocation:
                        value,

                    dropLat:
                        null,

                    dropLng:
                        null,

                    distance:
                        0,

                    estimatedDuration:
                        "",

                })
            );

        };


    /* =====================================
       CALCULATE ROUTE
    ===================================== */

    const calculateRoute =
        async () => {

            const {
                pickupLat,
                pickupLng,
                dropLat,
                dropLng,
            } = searchData;


            if (
                pickupLat === null ||
                pickupLng === null
            ) {

                setError(
                    "Please select a pickup location from the Google suggestions."
                );

                return null;

            }


            if (
                dropLat === null ||
                dropLng === null
            ) {

                setError(
                    "Please select a drop location from the Google suggestions."
                );

                return null;

            }


            if (
                !window.google ||
                !window.google.maps
            ) {

                setError(
                    "Google Maps is not loaded yet."
                );

                return null;

            }


            try {

                setCalculatingRoute(
                    true
                );

                setError("");


                const directionsService =
                    new window.google.maps
                        .DirectionsService();


                const result =
                    await directionsService
                        .route({

                            origin: {

                                lat:
                                    Number(
                                        pickupLat
                                    ),

                                lng:
                                    Number(
                                        pickupLng
                                    ),

                            },

                            destination: {

                                lat:
                                    Number(
                                        dropLat
                                    ),

                                lng:
                                    Number(
                                        dropLng
                                    ),

                            },

                            travelMode:
                                window.google
                                    .maps
                                    .TravelMode
                                    .DRIVING,

                        });


                const leg =
                    result
                        .routes?.[0]
                        ?.legs?.[0];


                if (
                    !leg
                ) {

                    throw new Error(
                        "Unable to calculate route."
                    );

                }


                const distanceInKm =
                    Number(
                        (
                            leg
                                .distance
                                .value /
                            1000
                        ).toFixed(2)
                    );


                const duration =
                    leg
                        .duration
                        ?.text ||
                    "";


                setSearchData(
                    (previous) => ({

                        ...previous,

                        distance:
                            distanceInKm,

                        estimatedDuration:
                            duration,

                    })
                );


                /*
                   IMPORTANT:

                   Return the latest route
                   data directly instead of
                   depending on async React state.
                */

                return {

                    distance:
                        distanceInKm,

                    duration,

                };


            } catch (error) {

                console.error(
                    "❌ ROUTE CALCULATION ERROR:",
                    error
                );


                setError(
                    "Unable to calculate distance between these locations."
                );


                return null;


            } finally {

                setCalculatingRoute(
                    false
                );

            }

        };


    /* =====================================
       SEARCH CABS
    ===================================== */

    const handleSearch =
        async () => {

            const {
                pickupLocation,
                dropLocation,
                numberOfPax,
                journeyDate,
                journeyTime,
            } = searchData;


            /*
               BASIC VALIDATION
            */

            if (
                !pickupLocation ||
                !dropLocation ||
                !numberOfPax ||
                !journeyDate ||
                !journeyTime
            ) {

                setError(
                    "Please fill all journey details."
                );

                return;

            }


            /*
               CALCULATE ROUTE
            */

            const routeData =
                await calculateRoute();


            if (
                !routeData
            ) {

                return;

            }


            try {

                setLoading(true);

                setSearched(false);

                setError("");

                setCabs([]);

                setOffers([]);


                /*
                   API CALL
                */

                const response =
                    await axios.get(

                        "/taxi-booking/search",

                        {

                            params: {

                                tenantId,

                                pickupLocation,

                                dropLocation,

                                numberOfPax:
                                    Number(
                                        numberOfPax
                                    ),

                                journeyDate,

                                journeyTime,

                                distance:
                                    routeData.distance,

                            },

                        }

                    );


                console.log(
                    "🚕 CAB SEARCH RESPONSE:",
                    response.data
                );


                if (
                    response.data
                        ?.success
                ) {

                    setCabs(
                        response.data
                            ?.data ||
                        []
                    );


                    setOffers(
                        response.data
                            ?.offers ||
                        []
                    );


                    setSearched(true);

                } else {

                    throw new Error(

                        response.data
                            ?.message ||

                        "Failed to search cabs."

                    );

                }


            } catch (error) {

                console.error(
                    "❌ CAB SEARCH ERROR:",
                    error
                );


                setError(

                    error
                        ?.response
                        ?.data
                        ?.message ||

                    error
                        ?.message ||

                    "Failed to search available cabs."

                );


                setSearched(true);


            } finally {

                setLoading(false);

            }

        };


    /* =====================================
       SELECT CAB
    ===================================== */

    const handleSelectCab =
        (cab) => {

            console.log(
                "🚕 SELECTED CAB:",
                cab
            );


            console.log(
                "📍 JOURNEY DETAILS:",
                searchData
            );


            /*
               STEP 5:

               Next we can create
               the Cab Booking Details /
               Booking Confirmation page.
            */

        };


    /* =====================================
       FORMAT PRICE
    ===================================== */

    const formatPrice =
        (amount) => {

            return Number(
                amount || 0
            ).toFixed(2);

        };


    /* =====================================
       GOOGLE MAPS ERROR
    ===================================== */

    if (
        loadError
    ) {

        return (

            <div
                className="container py-5"
            >

                <div
                    className="alert alert-danger"
                >

                    Failed to load Google Maps.

                    Please check your Google Maps API key and enabled APIs.

                </div>

            </div>

        );

    }


    /* =====================================
       PAGE
    ===================================== */

    return (

        <div
            style={{

                minHeight:
                    "100vh",

                background:
                    "#f5f7fb",

                padding:
                    "30px 15px",

            }}
        >

            <div
                className="container"
            >


                {/* HEADER */}

                <div
                    className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3"
                >

                    <div>

                        <Button
                            color="link"
                            className="p-0 text-decoration-none mb-2"
                            onClick={onBack}
                        >

                            <FaArrowLeft
                                className="me-2"
                            />

                            Back to Booking

                        </Button>


                        <h2
                            className="mb-1"
                        >

                            Book Your Cab

                        </h2>


                        <p
                            className="text-muted mb-0"
                        >

                            {
                                property?.name ||
                                property?.propertyName ||
                                "Find the perfect cab for your journey"
                            }

                        </p>

                    </div>


                    <div
                        className="text-muted"
                    >

                        <FaCar
                            className="me-2"
                        />

                        Safe & Comfortable Travel

                    </div>

                </div>


                {/* SEARCH CARD */}

                <Card
                    className="border-0 shadow-sm mb-5"
                    style={{

                        borderRadius:
                            "16px",

                    }}
                >

                    <CardBody
                        className="p-4"
                    >

                        <h5
                            className="mb-4"
                        >

                            Search Available Cabs

                        </h5>


                        <Row
                            className="g-3"
                        >


                            {/* PICKUP LOCATION */}

                            <Col
                                md="6"
                                lg="3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaMapMarkerAlt
                                        className="me-2 text-success"
                                    />

                                    Pickup Location

                                </label>


                                {isLoaded ? (

                                    <input
                                        ref={
                                            pickupInputRef
                                        }

                                        type="text"

                                        value={
                                            searchData.pickupLocation
                                        }

                                        onChange={
                                            handlePickupChange
                                        }

                                        placeholder="Search pickup location"

                                        autoComplete="off"

                                        className="form-control"

                                        style={{
                                            width:
                                                "100%",
                                        }}
                                    />

                                ) : (

                                    <Input
                                        disabled
                                        placeholder="Loading Google Maps..."
                                    />

                                )}

                            </Col>


                            {/* DROP LOCATION */}

                            <Col
                                md="6"
                                lg="3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaMapMarkerAlt
                                        className="me-2 text-danger"
                                    />

                                    Drop Location

                                </label>


                                {isLoaded ? (

                                    <input
                                        ref={
                                            dropInputRef
                                        }

                                        type="text"

                                        value={
                                            searchData.dropLocation
                                        }

                                        onChange={
                                            handleDropChange
                                        }

                                        placeholder="Search destination"

                                        autoComplete="off"

                                        className="form-control"

                                        style={{
                                            width:
                                                "100%",
                                        }}
                                    />

                                ) : (

                                    <Input
                                        disabled
                                        placeholder="Loading Google Maps..."
                                    />

                                )}

                            </Col>


                            {/* PASSENGERS */}

                            <Col
                                md="4"
                                lg="2"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaUsers
                                        className="me-2"
                                    />

                                    Passengers

                                </label>


                                <Input
                                    type="number"
                                    min="1"
                                    name="numberOfPax"

                                    value={
                                        searchData.numberOfPax
                                    }

                                    onChange={
                                        handleChange
                                    }
                                />

                            </Col>


                            {/* JOURNEY DATE */}

                            <Col
                                md="4"
                                lg="2"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaCalendarAlt
                                        className="me-2"
                                    />

                                    Journey Date

                                </label>


                                <Input
                                    type="date"
                                    name="journeyDate"

                                    value={
                                        searchData.journeyDate
                                    }

                                    onChange={
                                        handleChange
                                    }
                                />

                            </Col>


                            {/* JOURNEY TIME */}

                            <Col
                                md="4"
                                lg="2"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaClock
                                        className="me-2"
                                    />

                                    Journey Time

                                </label>


                                <Input
                                    type="time"
                                    name="journeyTime"

                                    value={
                                        searchData.journeyTime
                                    }

                                    onChange={
                                        handleChange
                                    }
                                />

                            </Col>


                            {/* DISTANCE */}

                            <Col
                                md="6"
                                lg="3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaRoute
                                        className="me-2"
                                    />

                                    Distance

                                </label>


                                <Input
                                    type="text"
                                    readOnly

                                    value={
                                        searchData.distance > 0
                                            ? `${searchData.distance} KM`
                                            : "Will be calculated automatically"
                                    }
                                />

                            </Col>


                            {/* ESTIMATED DURATION */}

                            <Col
                                md="6"
                                lg="3"
                            >

                                <label
                                    className="form-label fw-semibold"
                                >

                                    <FaClock
                                        className="me-2"
                                    />

                                    Estimated Duration

                                </label>


                                <Input
                                    type="text"
                                    readOnly

                                    value={
                                        searchData.estimatedDuration ||
                                        "Will be calculated automatically"
                                    }
                                />

                            </Col>


                            {/* SEARCH BUTTON */}

                            <Col
                                md="12"
                                lg="6"
                                className="d-flex align-items-end"
                            >

                                <Button
                                    color="primary"
                                    className="w-100"

                                    onClick={
                                        handleSearch
                                    }

                                    disabled={
                                        loading ||
                                        calculatingRoute ||
                                        !isLoaded
                                    }
                                >

                                    {
                                        loading ||
                                        calculatingRoute
                                            ? (

                                                <>

                                                    <Spinner
                                                        size="sm"
                                                        className="me-2"
                                                    />

                                                    {
                                                        calculatingRoute
                                                            ? "Calculating Route..."
                                                            : "Searching..."
                                                    }

                                                </>

                                            )
                                            : (

                                                <>

                                                    <FaSearch
                                                        className="me-2"
                                                    />

                                                    Search Cabs

                                                </>

                                            )
                                    }

                                </Button>

                            </Col>

                        </Row>


                        {/* ERROR */}

                        {error && (

                            <div
                                className="alert alert-danger mt-4 mb-0"
                            >

                                {error}

                            </div>

                        )}

                    </CardBody>

                </Card>


                {/* SEARCH RESULTS */}

                {searched && (

                    <div>


                        <div
                            className="d-flex justify-content-between align-items-center mb-4"
                        >

                            <div>

                                <h4
                                    className="mb-1"
                                >

                                    Available Cabs

                                </h4>


                                <p
                                    className="text-muted mb-0"
                                >

                                    {cabs.length}

                                    {" "}

                                    cab
                                    {cabs.length !== 1
                                        ? "s"
                                        : ""
                                    }

                                    {" "}

                                    found for your journey

                                </p>

                            </div>


                            {searchData.distance > 0 && (

                                <Badge
                                    color="light"
                                    className="text-dark border p-2"
                                >

                                    <FaRoute
                                        className="me-2"
                                    />

                                    {
                                        searchData.distance
                                    }

                                    {" "}

                                    KM

                                </Badge>

                            )}

                        </div>


                        {/* NO CABS */}

                        {cabs.length === 0 && (

                            <Card
                                className="border-0 shadow-sm"
                            >

                                <CardBody
                                    className="text-center p-5"
                                >

                                    <FaCar
                                        size={50}
                                        className="text-muted mb-3"
                                    />


                                    <h5>

                                        No Cabs Available

                                    </h5>


                                    <p
                                        className="text-muted mb-0"
                                    >

                                        No active cab matches your passenger requirement.

                                    </p>

                                </CardBody>

                            </Card>

                        )}


                        {/* CAB CARDS */}

                        <Row
                            className="g-4"
                        >

                            {cabs.map(
                                (cab) => (

                                    <Col
                                        md="6"
                                        lg="4"

                                        key={
                                            cab._id
                                        }
                                    >

                                        <Card
                                            className="h-100 border-0 shadow-sm"

                                            style={{

                                                borderRadius:
                                                    "14px",

                                                overflow:
                                                    "hidden",

                                            }}
                                        >


                                            {/* CAB IMAGE */}

                                            <div
                                                style={{

                                                    height:
                                                        "200px",

                                                    background:
                                                        "#f1f3f5",

                                                    position:
                                                        "relative",

                                                }}
                                            >

                                                {cab.image ? (

                                                    <img
                                                        src={
                                                            cab.image
                                                        }

                                                        alt={
                                                            cab.cabName
                                                        }

                                                        className="w-100 h-100"

                                                        style={{

                                                            objectFit:
                                                                "cover",

                                                        }}
                                                    />

                                                ) : (

                                                    <div
                                                        className="w-100 h-100 d-flex align-items-center justify-content-center"
                                                    >

                                                        <FaCar
                                                            size={55}
                                                            className="text-muted"
                                                        />

                                                    </div>

                                                )}


                                                <Badge
                                                    color="success"

                                                    style={{

                                                        position:
                                                            "absolute",

                                                        top:
                                                            "15px",

                                                        right:
                                                            "15px",

                                                    }}
                                                >

                                                    Available

                                                </Badge>

                                            </div>


                                            <CardBody
                                                className="p-4"
                                            >

                                                <div
                                                    className="mb-3"
                                                >

                                                    <h5
                                                        className="mb-1"
                                                    >

                                                        {
                                                            cab.cabName
                                                        }

                                                    </h5>


                                                    <div
                                                        className="text-muted"
                                                    >

                                                        {
                                                            cab.cabBrand
                                                        }

                                                        {" "}

                                                        {
                                                            cab.cabModel
                                                        }

                                                    </div>


                                                    <small
                                                        className="text-muted"
                                                    >

                                                        {
                                                            cab.cabType
                                                        }

                                                        {" • "}

                                                        {
                                                            cab.acType
                                                        }

                                                    </small>

                                                </div>


                                                <div
                                                    className="d-flex gap-4 mb-3 text-muted"
                                                >

                                                    <span>

                                                        <FaUsers
                                                            className="me-1"
                                                        />

                                                        {
                                                            cab.seatingCapacity
                                                        }

                                                        {" "}

                                                        Seats

                                                    </span>


                                                    <span>

                                                        <FaSuitcase
                                                            className="me-1"
                                                        />

                                                        {
                                                            cab.luggageCapacity
                                                        }

                                                        {" "}

                                                        Bags

                                                    </span>

                                                </div>


                                                {/* OFFER */}

                                                {cab.bestOffer && (

                                                    <div
                                                        className="border rounded p-2 mb-3 bg-light"
                                                    >

                                                        <div
                                                            className="fw-bold text-success"
                                                        >

                                                            <FaTag
                                                                className="me-2"
                                                            />

                                                            {
                                                                cab.bestOffer.code
                                                            }

                                                        </div>


                                                        {cab.bestOffer.description && (

                                                            <small>

                                                                {
                                                                    cab.bestOffer.description
                                                                }

                                                            </small>

                                                        )}

                                                    </div>

                                                )}


                                                {/* PRICE */}

                                                <div
                                                    className="border-top pt-3"
                                                >

                                                    <div
                                                        className="d-flex justify-content-between align-items-end"
                                                    >

                                                        <div>

                                                            <small
                                                                className="text-muted"
                                                            >

                                                                Total Fare

                                                            </small>


                                                            {cab.pricing
                                                                ?.discountAmount >
                                                                0 && (

                                                                <div
                                                                    className="text-muted text-decoration-line-through"
                                                                >

                                                                    ₹
                                                                    {
                                                                        formatPrice(
                                                                            cab.pricing
                                                                                ?.originalPrice
                                                                        )
                                                                    }

                                                                </div>

                                                            )}


                                                            <h4
                                                                className="mb-0"
                                                            >

                                                                ₹
                                                                {
                                                                    formatPrice(
                                                                        cab.pricing
                                                                            ?.finalPrice
                                                                    )
                                                                }

                                                            </h4>

                                                        </div>


                                                        {cab.pricing
                                                            ?.discountAmount >
                                                            0 && (

                                                            <small
                                                                className="text-success"
                                                            >

                                                                Save ₹
                                                                {
                                                                    formatPrice(
                                                                        cab.pricing
                                                                            ?.discountAmount
                                                                    )
                                                                }

                                                            </small>

                                                        )}

                                                    </div>

                                                </div>


                                                {/* SELECT CAB */}

                                                <Button
                                                    color="primary"
                                                    className="w-100 mt-4"

                                                    onClick={
                                                        () =>
                                                            handleSelectCab(
                                                                cab
                                                            )
                                                    }
                                                >

                                                    Select This Cab

                                                </Button>

                                            </CardBody>

                                        </Card>

                                    </Col>

                                )
                            )}

                        </Row>

                    </div>

                )}

            </div>

        </div>

    );

};


export default
    CabBookingSearchPage;